import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Navigation, MapPin, CalendarCheck, Loader2, Clock } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, MAPS_URL, ADDRESS, WORKING_HOURS } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Диагностика", "Стартови устройства", "Механика", "Автоклиматици", "Автолампи",
  "Обслужване", "Части и консумативи", "Акумулатори", "Зарядни устройства", "Друго",
];

const inputCls =
  "w-full border border-white/10 bg-[#121212] px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-[#39FF14] focus:ring-1 focus:ring-[#39FF14]";

export default function Booking() {
  const [form, setForm] = useState({ name: "", phone: "", car: "", service: "", preferred_date: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const todayStr = new Date().toISOString().split("T")[0];

  const setDate = (e) => {
    const v = e.target.value;
    if (v) {
      const day = new Date(v + "T00:00:00").getDay();
      if (day === 0 || day === 6) {
        toast.error("В събота и неделя сервизът не работи. Моля, изберете ден от понеделник до петък.");
        return;
      }
    }
    setForm({ ...form, preferred_date: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Моля, попълнете име и телефон.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/appointments`, form);
      toast.success("Заявката е изпратена! Ще се свържем с вас за потвърждение.");
      setForm({ name: "", phone: "", car: "", service: "", preferred_date: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Възникна грешка. Моля, обадете се на " + PHONE_DISPLAY);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="booking" className="relative overflow-hidden border-t border-white/10 bg-[#0A0A0A] py-24 lg:py-32" data-testid="booking-section">
      <div className="hero-grid-lines absolute inset-0" />
      <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-[#39FF14]/10 blur-[140px]" />

      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 lg:grid-cols-2 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-[#39FF14]">
            <span className="font-display text-stroke-green text-3xl">04</span> Запиши час
          </p>
          <h2 className="font-display text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-5xl">
            Запазете своя <span className="text-[#39FF14]">час</span> още сега
          </h2>
          <p className="mt-6 max-w-md text-base text-zinc-400">
            Попълнете формата и ние ще се свържем с вас за потвърждение. За спешни случаи —
            обадете се директно.
          </p>

          <div className="mt-10 space-y-5">
            <a
              data-testid="booking-call-button"
              href={PHONE_TEL}
              className="group flex items-center gap-4 border border-white/10 bg-[#0D0D0D] p-5 transition-colors duration-300 hover:border-[#39FF14]/50"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-[#008A22] text-white transition-colors duration-300 group-hover:bg-[#39FF14] group-hover:text-black">
                <Phone size={22} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Телефон</p>
                <p className="font-display text-lg font-bold text-white">{PHONE_DISPLAY}</p>
              </div>
            </a>
            <a
              data-testid="booking-navigate-button"
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 border border-white/10 bg-[#0D0D0D] p-5 transition-colors duration-300 hover:border-[#39FF14]/50"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-[#008A22] text-white transition-colors duration-300 group-hover:bg-[#39FF14] group-hover:text-black">
                <Navigation size={22} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Адрес</p>
                <p className="text-sm font-semibold text-white">{ADDRESS}</p>
              </div>
            </a>
            <div
              data-testid="booking-working-hours"
              className="flex items-start gap-4 border border-white/10 bg-[#0D0D0D] p-5"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-[#008A22] text-white">
                <Clock size={22} />
              </div>
              <div className="w-full">
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Работно време</p>
                <ul className="mt-2 space-y-1 text-sm">
                  {WORKING_HOURS.map((w) => (
                    <li key={w.day} className="flex items-center justify-between gap-6 max-w-[260px]">
                      <span className="text-zinc-400">{w.day}</span>
                      <span className={w.hours === "Затворено" ? "text-zinc-600" : "font-semibold text-white"}>
                        {w.hours}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="booking-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="border border-white/10 bg-[#0D0D0D] p-8 lg:p-10"
        >
          <div className="mb-6 flex items-center gap-3">
            <CalendarCheck size={22} className="text-[#39FF14]" />
            <h3 className="font-display text-lg font-bold uppercase tracking-wide">Форма за записване</h3>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="bk-name" className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">Име *</label>
              <input id="bk-name" data-testid="booking-name-input" required value={form.name} onChange={set("name")} placeholder="Вашето име" className={inputCls} />
            </div>
            <div>
              <label htmlFor="bk-phone" className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">Телефон *</label>
              <input id="bk-phone" data-testid="booking-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="08XX XXX XXX" className={inputCls} />
            </div>
            <div>
              <label htmlFor="bk-car" className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">Автомобил</label>
              <input id="bk-car" data-testid="booking-car-input" value={form.car} onChange={set("car")} placeholder="Марка и модел" className={inputCls} />
            </div>
            <div>
              <label htmlFor="bk-service" className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">Услуга</label>
              <select id="bk-service" data-testid="booking-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                <option value="">Изберете услуга</option>
                {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="bk-date" className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">Предпочитана дата <span className="normal-case text-zinc-600">(Пон – Пет)</span></label>
              <input id="bk-date" data-testid="booking-date-input" type="date" min={todayStr} value={form.preferred_date} onChange={setDate} className={`${inputCls} [color-scheme:dark]`} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="bk-message" className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">Съобщение</label>
              <textarea id="bk-message" data-testid="booking-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Опишете проблема или запитването си..." className={`${inputCls} resize-none`} />
            </div>
          </div>

          <button
            data-testid="booking-submit-button"
            type="submit"
            disabled={sending}
            className="green-glow mt-8 flex w-full items-center justify-center gap-3 bg-[#39FF14] px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-black transition-colors duration-300 hover:bg-[#32E612] disabled:opacity-60"
          >
            {sending ? <Loader2 size={18} className="animate-spin" /> : <CalendarCheck size={18} strokeWidth={2.5} />}
            {sending ? "Изпращане..." : "Изпрати заявка"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
